import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

/**
 * Render Document as Text Step
 *
 * Takes a tabular document (CSV or XLSX) and emits its contents as a plain
 * string socket, suitable for piping into AI prompts, emails, or logs.
 *
 * Two output formats are supported via `format`:
 *  - `csv`: raw CSV text (pass-through for CSV inputs; first-sheet CSV for XLSX)
 *  - `markdown`: GitHub-flavored Markdown table (first row treated as header)
 */
export const renderDocumentAsTextStep = defineStepConfig({
  type: "render_document_as_text",
  name: "Render Document as Text",
  description:
    "Render a CSV/XLSX document as plain text (CSV or Markdown table)",
  category: "Transformation",

  inputSchema: z.object({
    inputDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "CSV or XLSX document to render",
    ),
  }),

  outputSchema: z.object({
    text: z.string().describe("Rendered text contents of the document"),
    format: z
      .enum(["csv", "markdown"])
      .describe("Format the text was rendered in (passthrough of config)"),
    rowCount: z
      .number()
      .describe(
        "Number of data rows in the rendered output (excludes the header row)",
      ),
  }),

  configSchema: z.object({
    format: z
      .enum(["csv", "markdown"])
      .prefault("csv")
      .describe(
        'Output text format. "csv" emits raw CSV; "markdown" emits a GitHub-flavored Markdown table.',
      ),
    maxRows: z
      .number()
      .int()
      .positive()
      .optional()
      .describe(
        "Optional cap on the number of data rows. Useful to keep string-socket payloads small.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 1,
    },
  },
});
